import { createSlice, type PayloadAction } from "@reduxjs/toolkit";

import {
  fetchEmails,
  fetchEmailById,
  toggleEmailFlag,
} from "../thunks/emailThunks";

import type { UIMessage } from "../../hooks/useAiAssistant";
import type { EmailListItem, EmailDetails, MashedEmail } from "../../utils/types/email";

export interface AiReviewData {
  content: string;
  suggestions: string[];
  hasGenerated: boolean;
  timestamp: number;
}

export interface AiChatData {
  messages: UIMessage[];
  lastUpdated: number;
}

interface EmailState {
  emails: EmailListItem[];
  selectedEmailId: string | null;
  selectedEmailDetails: EmailDetails | null;
  currentMashedEmail:MashedEmail|null;
  currentFolder: string;
  listLoading: boolean;
  detailsLoading: boolean;
  error: string | null;
  unreadCount: number;
  aiReviews: Record<string, AiReviewData>;
  aiChats: Record<string, AiChatData>;
  drafts: Record<string, string>;
}

const initialState: EmailState = {
  emails: [],
  selectedEmailId: null,
  selectedEmailDetails: null,
  currentMashedEmail:null,
  currentFolder: "", // Start empty - wait for folder selection
  listLoading: false,
  detailsLoading: false,
  error: null,
  unreadCount: 0,
  aiReviews: {},
  aiChats: {},
  drafts: {},
};

const emailSlice = createSlice({
  name: "emails",
  initialState,
  reducers: {
    selectEmail: (state, action: PayloadAction<{
      emailId: string | null;
      mashedEmail:MashedEmail|null;
    }|null>) => {
      state.selectedEmailId = action.payload?.emailId||null;
      state.currentMashedEmail=action.payload?.mashedEmail||null;
      if (!action.payload) {
        state.selectedEmailDetails = null;
      }
    },
    changeFolder: (state, action: PayloadAction<string>) => {
      state.currentFolder = action.payload;
      // Only clear selected email if this is a user-initiated folder change
      // (indicated by having a currentMashedEmail - pre-navigation selections have null)
      if (state.currentMashedEmail !== null) {
        state.selectedEmailId = null;
        state.selectedEmailDetails = null;
        state.currentMashedEmail = null;
      }
    },

    resetSelectedEmail: (state) => {  
      state.selectedEmailId = null;
      state.selectedEmailDetails = null;
      state.currentMashedEmail=null;
    },

    setAiReview: (
      state,
      action: PayloadAction<{
        emailId: string;
        content: string;
        suggestions: string[];
      }>
    ) => {
      const { emailId, content, suggestions } = action.payload;
      state.aiReviews[emailId] = {
        content,
        suggestions,
        hasGenerated: true,
        timestamp: Date.now(),
      };
    },
    addMessageToChat: (
      state,
      action: PayloadAction<{ emailId: string; message: UIMessage }>
    ) => {
      const { emailId, message } = action.payload;
      if (!state.aiChats[emailId]) {
        state.aiChats[emailId] = { messages: [], lastUpdated: Date.now() };
      }
      state.aiChats[emailId].messages.push(message);
      state.aiChats[emailId].lastUpdated = Date.now();
    },

    setAiChatHistory: (
      state,
      action: PayloadAction<{ emailId: string; messages: UIMessage[] }>
    ) => {
      const { emailId, messages } = action.payload;
      state.aiChats[emailId] = {
        messages,
        lastUpdated: Date.now(),
      };
    },

    clearAiData: (state) => {
      state.aiReviews = {};
      state.aiChats = {};
    },
    setEmailDraft: (
      state,
      action: PayloadAction<{ emailId: string; content: string }>
    ) => {
      const { emailId, content } = action.payload;
      state.drafts[emailId] = content;
    },

    clearEmailDraft: (state, action: PayloadAction<string>) => {
      delete state.drafts[action.payload];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchEmails.pending, (state) => {
        state.listLoading = true;
        state.error = null;
      })
      .addCase(
        fetchEmails.fulfilled,
        (state, action: PayloadAction<EmailListItem[]>) => {
          state.listLoading = false;
          state.emails = action.payload;
          state.unreadCount = action.payload.filter(
            (email) => !email.isRead
          ).length;
        }
      )
      .addCase(fetchEmails.rejected, (state, action) => {
        state.listLoading = false;
        state.error = action.payload as string;
      })
      .addCase(fetchEmailById.pending, (state) => {
        state.detailsLoading = true;
        state.selectedEmailDetails = null;
        state.error = null;
      })
      .addCase(
        fetchEmailById.fulfilled,
        (state, action: PayloadAction<EmailDetails>) => {
          state.detailsLoading = false;
          const details = action.payload;
          if (details.attachments) {
            details.attachments = details.attachments.filter(
              (a) => !a.name?.toLowerCase().endsWith(".p7m") && !a.isInline
            );
          }
          state.selectedEmailDetails = details;
        }
      )
      .addCase(fetchEmailById.rejected, (state, action) => {
        state.detailsLoading = false;
        state.error = action.payload as string;
      })
      .addCase(
        toggleEmailFlag.fulfilled,
        (state, action: PayloadAction<EmailDetails>) => {
          if (
            state.selectedEmailDetails &&
            state.selectedEmailDetails.id === action.payload.id
          ) {
            state.selectedEmailDetails = action.payload;
          }
        }
      );
  },
});

export const {
  selectEmail,
  changeFolder,
  setAiReview,
  addMessageToChat,
  setAiChatHistory,
  clearAiData,
  setEmailDraft,
  clearEmailDraft,
  resetSelectedEmail
} = emailSlice.actions;
export default emailSlice.reducer;
